import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AccordionFullTable, CustomDelByNote, SelectActions } from 'components';
import { FindSuccessItem, Images, useModal } from 'utils';
import { claimPaymentActions, httpRequestsOnSuccessActions } from 'store';
import { claimPaymentTableInfo, remittanceStatusEnums } from './constants';
import { AddClaimPayment, ClaimPaymentTotals, CompletedClaimFilters } from './core';

const VERIFY_ACTION_TYPE = 'VERIFY_REMITTANCES';
const MASS_VOID_ACTION_TYPE = 'MASS_VOID_REMITTANCE';
const MARK_REVIEWED_ACTION_TYPE = 'MARK_REVIEWED_REMITTANCE';
export const ClaimPayments = ({}) => {
    const remittances = useSelector((state) => state.claimPayment.remittances);
    const dispatch = useDispatch();
    const [selectedList, setSelectedList] = useState([]);
    const location = useLocation();
    const info = location?.state;
    const successVerify = FindSuccessItem(VERIFY_ACTION_TYPE);
    const { open } = useModal();
    const [activeRow, setActiveRow] = useState(null);

    const renderParams = () => {
        const sendInfo = {
            ...info,
        };
        delete sendInfo.page;
        // Display-only companions — the API filters on `payer` / `client` ids, and forwarding
        // these sends a stray name/object as a query param.
        delete sendInfo.funderName;
        delete sendInfo.clientFull;
        sendInfo.limit = 50;
        sendInfo.skip = info?.skip ? info?.skip : 0;


        if (!info?.statuses) {
            sendInfo.statuses = [remittanceStatusEnums.OPEN, remittanceStatusEnums.APPLIED];
        } else if (info?.statuses?.length) {
            info?.statuses?.length ? (sendInfo.statuses = info.statuses?.map((i) => i?.id || i)) : delete sendInfo.statuses;
            // sendInfo.statuses = info?.statuses;
        } else {
            delete sendInfo.statuses;
        }
        delete sendInfo.tabType;
        return sendInfo;
    };

    const getClaimPayments = (load) => {
        dispatch(claimPaymentActions.getRemittance(renderParams(), load));

        const params = {
            ...renderParams(),
        };
        delete params.skip;
        delete params.limit;
        delete params.sortType;
        dispatch(claimPaymentActions.getRemittanceTotal(params));
    };

    useEffect(() => {
        getClaimPayments();
    }, [info]);

    useEffect(() => {
        if (successVerify) {
            getClaimPayments('noLoad');
            removeSelectedList();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(VERIFY_ACTION_TYPE));
        }
    }, [successVerify]);

    const handleCheck = (ev, item, name) => {
        ev.stopPropagation();
        ev.preventDefault();

        if (name === 'all' || name === 'selectAll') {
            let allList = remittances?.remittances?.length === selectedList?.length ? [] : remittances?.remittances?.map((i) => i?.id);
            setSelectedList(allList);
        } else {
            const list = [...selectedList];
            const uniqSet = new Set(list);

            if (!uniqSet.has(item?.id)) {
                list.push(item?.id);
            } else {
                list.indexOf(item?.id) !== -1 && list.splice(list.indexOf(item?.id), 1);
            }
            setSelectedList(list);
        }
    };

    const removeSelectedList = () => {
        setSelectedList([]);
    };

    const allSelectedVoided =
        !!selectedList?.length &&
        selectedList.every(
            (id) => remittances?.remittances?.find((r) => r?.id === id)?.status === remittanceStatusEnums.VOID
        );

    // Only remittances flagged `needsReview` can be marked reviewed. The Review action stays enabled
    // as long as at least one selected row still needs review; otherwise it dims (opacity 0.5).
    const reviewableIds = (selectedList || []).filter(
        (id) => remittances?.remittances?.find((r) => r?.id === id)?.needsReview
    );
    const canReviewSelected = !!reviewableIds.length;

    const handleMassVoid = () => {
        if (allSelectedVoided) return;
        open(
            <CustomDelByNote
                info="Void Payments?"
                text={`You are about to void ${selectedList?.length} payment${selectedList?.length === 1 ? '' : 's'}. This will revert all applied claims to unpaid status. This action cannot be undone.`}
                handleDel={(closingComment) => {
                    dispatch(claimPaymentActions.massVoidRemittance({ remittanceIds: selectedList, reason: closingComment }));
                }}
                afterSuccess={() => {
                    getClaimPayments('noLoad');
                    removeSelectedList();
                }}
                required={false}
                innerText={'Void Payments'}
                inputLabel={'Void Reason (optional)'}
                actionType={MASS_VOID_ACTION_TYPE}
            />
        );
    };

    const handleMassReview = () => {
        if (!canReviewSelected) return;
        open(
            <CustomDelByNote
                info="Mark as Reviewed?"
                text={`You are about to mark ${reviewableIds.length} remittance${
                    reviewableIds.length === 1 ? '' : 's'
                } as reviewed. This clears the review flag created during ERA ingestion.`}
                handleDel={(reviewNote) => {
                    dispatch(claimPaymentActions.markReviewedRemittance(reviewableIds, reviewNote));
                }}
                afterSuccess={() => {
                    getClaimPayments('noLoad');
                    removeSelectedList();
                }}
                required={false}
                innerText={'Mark Reviewed'}
                inputLabel={'Review Note (optional)'}
                actionType={MARK_REVIEWED_ACTION_TYPE}
                color={'blue'}
            />
        );
    };

    // const handleNavigate = (id) => {
    //     navigate(`/claim-payment/${id}`);
    // };

    const handleAction = (name, item) => {
        if (name === 'edit') {
            open(<AddClaimPayment afterSuccess={() => getClaimPayments('noLoad')} info={item} edit={true} />, {
                styles: { headerStyle: { display: 'none' } },
            });
        }
        if (name === 'down') {
            setActiveRow(item?.id === activeRow ? ' ' : item?.id);
        }
    };

    return (
        <>
            <div>
                {/*{RolePermission([PermissionList.CLAIMS_CREATE?.code]) && !!selectedList?.length &&*/}
                {!!selectedList?.length && (
                    <SelectActions
                        submitText={'Verify'}
                        submitBtnIcon={Images.verified}
                        selectInnetText={selectedList?.length === remittances?.remittances?.length ? 'Clear Selection' : 'Select All'}
                        actionType={VERIFY_ACTION_TYPE}
                        selectedList={selectedList?.length}
                        selectAll={handleCheck}
                        submitClaims={() => {
                            dispatch(claimPaymentActions.verifyRemittance(selectedList));
                        }}
                        removeSelected={removeSelectedList}
                        renderOtherBtns={
                            <div className='flex-align-center' style={{gap: 8}}>
                                <button
                                    className="select-all-btn"
                                    style={{ opacity: canReviewSelected ? 1 : 0.5 }}
                                    disabled={!canReviewSelected}
                                    onClick={handleMassReview}>
                                    <img src={Images.eyeWhite} alt="icon" />
                                    Review
                                </button>
                                <button
                                    className="select-all-btn"
                                    style={{ color: '#DE350B', opacity: allSelectedVoided ? 0.5 : 1 }}
                                    disabled={allSelectedVoided}
                                    onClick={handleMassVoid}>
                                    <img src={Images.voidCircle} alt="icon" />
                                    Void
                                </button>
                            </div>
                        }
                    />
                )}
                <CompletedClaimFilters afterSuccess={() => getClaimPayments('noLoad')} />

                <ClaimPaymentTotals />

                <AccordionFullTable
                    handleAccordion={() => {
                        setActiveRow(null);
                    }}
                    margin={'0'}
                    tableInfo={claimPaymentTableInfo(
                        handleAction,
                        remittances?.remittances,
                        selectedList,
                        handleCheck,
                        getClaimPayments,
                        activeRow
                    )}
                    list={remittances?.remittances}
                    listCount={remittances?.count}
                    // notShowDown={true}
                    defaultOpen={activeRow ? remittances?.remittances?.findIndex((item) => item?.id === activeRow) : null}
                    detailPage={(item) => `/claim-payment/${item?.id}`}
                    selectedList={selectedList}
                />
            </div>
        </>
    );
};
